import React, { useEffect, useRef } from "react";
import { Box, Group, Pagination } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

export interface PaginatedShellProps {
  /** One ReactNode per page; the shell renders only the active one. */
  pages: React.ReactNode[];
  /** Active page index. Caller owns the state so it survives re-renders. */
  activeIndex: number;
  onChange: (next: number) => void;
  /** Optional max width for the inner content area. */
  maxWidth?: number | string;
  /** Applied to the inner content box (turn it into a card, add padding, etc). */
  contentStyle?: React.CSSProperties;
  /** Rendered once, above the swapping page content and dots. */
  header?: React.ReactNode;
}

/** Shared paginated layout for preview/viewer with centered bottom controls. */
export const PaginatedShell: React.FC<PaginatedShellProps> = ({
  pages,
  activeIndex,
  onChange,
  maxWidth = 800,
  contentStyle,
  header,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const total = pages.length;
  const safeIndex = Math.min(Math.max(activeIndex, 0), Math.max(0, total - 1));
  const goPrev = () => onChange(Math.max(0, safeIndex - 1));
  const goNext = () => onChange(Math.min(total - 1, safeIndex + 1));

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Only treat as a swipe if movement is mostly horizontal — otherwise
    // it was probably a vertical scroll gesture.
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  useEffect(() => {
    // Clamp if pages shrink below the active index.
    if (activeIndex > total - 1) onChange(Math.max(0, total - 1));
  }, [total, activeIndex, onChange]);

  return (
    <Box style={{ position: "relative" }}>
      <Group
        align="center"
        justify="center"
        gap={0}
        wrap="nowrap"
        style={{ width: "100%" }}
      >
        <Box
          onTouchStart={isMobile ? onTouchStart : undefined}
          onTouchEnd={isMobile ? onTouchEnd : undefined}
          style={{
            flex: 1,
            maxWidth,
            minWidth: 0,
            // Hint to the browser that vertical pan is allowed but horizontal
            // gestures are ours to handle.
            touchAction: isMobile ? "pan-y" : undefined,
            ...contentStyle,
          }}
        >
          {header}
          {pages[safeIndex]}
          {total > 1 && (
            <Group justify="center" mt="lg" mb="xs">
              <Pagination
                total={total}
                value={safeIndex + 1}
                onChange={(page) => onChange(page - 1)}
                withEdges={false}
                siblings={0}
                boundaries={1}
              />
            </Group>
          )}
        </Box>
      </Group>
    </Box>
  );
};
